import { useMemo, useRef, useState } from 'react';
import type { StepKind } from '@arcnet/contracts';
import { useSession } from '../../lib/auth/session.js';
import { useStepHistory, useStepVersion } from '../../lib/api/hooks-tms.js';
import { Modal } from '../../components/ui/Modal.js';
import { Icon } from '../../components/ui/Icon.js';
import { ErrorBanner } from '../../components/ui/Primitives.js';
import { useLiveRouting } from './flows/useLive.js';
import { namesFrom } from './flows/describe.js';
import { EachSave, HistoryList, NameVersion, StepChanges, historySince } from './flows/HistoryList.js';
import { StepRestoreDialog, UsedByNote } from './flows/RestoreDialog.js';
import { clock, rowsOf, shortAt, sourceText } from './flows/history.js';

/**
 * A step's own history on its Classic page (docs/41 §12.2; Fable's mockup 6): from the editor's head,
 * a window with the versions that changed this step on the left, and what the chosen one changed on
 * the right, with the routes that use the step. No diagram: the step is one card. Restoring writes
 * it at once, as a save on the page does.
 *
 * `onRestored` runs when the window closes after a restore, so the page reloads the step from what
 * was written: its form still holds the settings it opened with.
 */
export function StepHistory({ tenantId, kind, id, name, onRestored }: {
  tenantId: string; kind: StepKind; id: string; name: string; onRestored: () => void;
}) {
  const can = useSession((s) => s.can);
  const [open, setOpen] = useState(false);
  const restored = useRef(false);
  const opener = useRef<HTMLButtonElement>(null);
  if (!can('flow:read')) return null;
  const close = () => {
    setOpen(false);
    requestAnimationFrame(() => opener.current?.focus());
    if (restored.current) { restored.current = false; onRestored(); }
  };
  return (
    // The IVR and call step editors draw their head inside their form: a form in this window (naming a
    // version) must not submit theirs, which React would otherwise bubble its submit to.
    <span onSubmit={(e) => e.stopPropagation()}>
      <button ref={opener} type="button" className="arc-btn arc-btn-ghost arc-btn-sm" aria-expanded={open} onClick={() => setOpen(true)}>
        <Icon name="history" size={13} />History
      </button>
      {open && (
        <StepHistoryWindow tenantId={tenantId} kind={kind} id={id} name={name}
          canName={can('flow:write')} canPublish={can('flow:publish')}
          onRestored={() => { restored.current = true; }} onClose={close} />
      )}
    </span>
  );
}

function StepHistoryWindow({ tenantId, kind, id, name, canName, canPublish, onRestored, onClose }: {
  tenantId: string; kind: StepKind; id: string; name: string; canName: boolean; canPublish: boolean;
  onRestored: () => void; onClose: () => void;
}) {
  const { live } = useLiveRouting(tenantId);
  const historyQ = useStepHistory(tenantId, kind, id);
  const [at, setAt] = useState<string | null>(null);
  const versionQ = useStepVersion(tenantId, kind, id, at);
  const [restoring, setRestoring] = useState(false);
  const versions = useMemo(() => historyQ.data?.data ?? [], [historyQ.data]);
  const rows = useMemo(() => rowsOf(versions), [versions]);
  const row = rows.find((r) => r.id === (at ?? rows[0]?.id)) ?? null;
  const viewing = at && versionQ.data?.version.id === at ? versionQ.data : null;

  // Oldest first, so the step's newest name is the one its lines use.
  const names = useMemo(() => namesFrom(live,
    [...versions].reverse().flatMap((v) => v.steps.map((s) => ({ kind: s.kind, id: s.id, body: s.after ?? s.before })))), [live, versions]);

  const v = row?.versions[0] ?? null;
  return (
    <Modal title={`History · ${name}`} size="md" onClose={onClose} actions={canPublish && at && viewing ? (
      <button type="button" className="arc-btn arc-btn-primary arc-btn-sm" onClick={() => setRestoring(true)}>
        <Icon name="restore" size={13} />Restore this version
      </button>
    ) : undefined}>
      <div className="arc-history-modal arc-history-modal-step">
        <div className="arc-history-side">
          {versions.length > 0 && (
            <div className="arc-subtle" style={{ padding: '10px 14px 0', fontSize: 12 }}>
              {historySince(versions).replace(/^since/, 'Since')}
            </div>
          )}
          {historyQ.error ? <div style={{ padding: 14 }}><ErrorBanner error={historyQ.error} /></div>
            : historyQ.isLoading ? <div style={{ padding: 14 }}><div className="arc-skeleton" style={{ height: 160 }} /></div>
              : <HistoryList tenantId={tenantId} versions={versions} names={names} viewing={at} onView={setAt}
                  canName={canName} inline={false} withRoute />}
        </div>
        <div className="arc-history-main">
          {row && v && (
            <div className="arc-history-changes">
              <div className="arc-history-changes-head">
                <div className="arc-stack">
                  <strong>{row.versions.length > 1 ? `${shortAt(row.versions[row.versions.length - 1]!.at)}–${clock(v.at)}` : shortAt(v.at)}</strong>
                  <span className="arc-muted">
                    {[v.by, sourceText(v, true), row.versions.length > 1 ? `${row.versions.length} saves` : null].filter(Boolean).join(' · ')}
                  </span>
                </div>
                {canName && <NameVersion key={`${v.id}:${v.name ?? ''}`} tenantId={tenantId} version={v} />}
              </div>
              {v.restored_from && (
                <p className="arc-history-restore-note">
                  <Icon name="restore" size={12} />
                  Went back to {shortAt(v.restored_from.at)}{v.restored_from.name ? ` · ${v.restored_from.name}` : ''}.
                </p>
              )}
              {v.source === 'start'
                ? <p className="arc-muted" style={{ margin: 0, fontSize: 12.5 }}>History starts here: the step as it was when version history began.</p>
                : <StepChanges steps={row.steps} names={names} />}
              {row.versions.length > 1 && <EachSave row={row} names={names} />}
              {historyQ.data && <UsedByNote kind={kind} usedBy={historyQ.data.used_by} />}
            </div>
          )}
        </div>
      </div>
      {restoring && viewing && at && (
        <StepRestoreDialog tenantId={tenantId} kind={kind} id={id} name={name} versionId={at} at={viewing.version.at}
          plan={viewing.plan} names={names} onClose={() => setRestoring(false)}
          onRestored={() => { setRestoring(false); setAt(null); onRestored(); }} />
      )}
    </Modal>
  );
}
